import { useState, useEffect, useMemo } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, Send, User, Clock, MessageSquare, CheckCircle, XCircle, UserPlus } from "lucide-react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getTicketWithReplies,
  replyToTicketAsAdmin,
  updateTicketStatus,
  selectCurrentTicket,
  selectCurrentTicketLoading,
  selectCurrentTicketError,
  clearCurrentTicket,
} from "@/store/slices/ticketSlice"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"

export default function TicketDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { assignTicketToAdmin, getAllAdmins } = useAdminAuth()
  const ticketData = useAppSelector(selectCurrentTicket)
  const loading = useAppSelector(selectCurrentTicketLoading)
  const error = useAppSelector(selectCurrentTicketError)
  const [replyContent, setReplyContent] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [changingStatus, setChangingStatus] = useState(false)
  const [admins, setAdmins] = useState([])
  const [assigning, setAssigning] = useState(false)
  const [loadingAdmins, setLoadingAdmins] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(getTicketWithReplies(id))
    }
    return () => {
      dispatch(clearCurrentTicket())
    }
  }, [id, dispatch])

  // Load admins for assignment
  useEffect(() => {
    const loadAdmins = async () => {
      setLoadingAdmins(true)
      try {
        const data = await getAllAdmins(1, 100, "")
        setAdmins(data.admins || [])
      } catch (error) {
        console.error("Error loading admins:", error)
      } finally {
        setLoadingAdmins(false)
      }
    }
    loadAdmins()
  }, [getAllAdmins])

  // Extract ticket and messages from Redux state
  const ticket = useMemo(() => ticketData?.ticket || null, [ticketData])
  const messages = useMemo(() => ticketData?.messages || [], [ticketData])
  const currentStatus = useMemo(() => ticket?.status || "", [ticket])

  // Navigate back on error
  useEffect(() => {
    if (error && !loading) {
      navigate("/admin/support")
    }
  }, [error, loading, navigate])

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-green-100 text-green-700"
      case "Pending":
        return "bg-yellow-100 text-yellow-700"
      case "Closed":
        return "bg-gray-100 text-gray-700"
      case "Resolved":
        return "bg-blue-100 text-blue-700"
      case "Answered":
        return "bg-purple-100 text-purple-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  }

  // Flatten nested messages for display
  const flattenMessages = (messagesArray, level = 0) => {
    const result = []
    messagesArray.forEach((msg) => {
      result.push({ ...msg, level })
      if (msg.children && msg.children.length > 0) {
        result.push(...flattenMessages(msg.children, level + 1))
      }
    })
    return result
  }

  // Get all messages: initial description + messages
  const getAllMessages = () => {
    const allMessages = []

    // Add initial ticket description as first message
    if (ticket?.description) {
      allMessages.push({
        id: "initial",
        message: ticket.description,
        created_at: ticket.created_at,
        sender: ticket.createdBy
          ? `${ticket.createdBy.firstName || ""} ${ticket.createdBy.lastName || ""}`.trim() || ticket.createdBy.email || "Customer"
          : "Customer",
        isAdmin: false,
        level: 0,
      })
    }

    // Add flattened messages
    const flattenedMessages = flattenMessages(messages)
    flattenedMessages.forEach((msg) => {
      const isAdmin = msg.sender_type === "admin"
      const senderName = isAdmin
        ? msg.sender_name || "Admin"
        : msg.sender_name && msg.sender_last_name
        ? `${msg.sender_name} ${msg.sender_last_name}`.trim()
        : msg.sender_email || "Customer"

      allMessages.push({
        id: msg.id,
        message: msg.message,
        created_at: msg.created_at,
        sender: senderName,
        isAdmin: isAdmin,
        level: msg.level || 0,
      })
    })

    return allMessages
  }

  const handleSendReply = async () => {
    if (!replyContent.trim() || submitting || !id) return

    setSubmitting(true)
    try {
      await dispatch(replyToTicketAsAdmin({
        ticketId: id,
        message: replyContent.trim(),
      })).unwrap()
      setReplyContent("")
      // Ticket details will be refreshed automatically by Redux
    } catch (error) {
      console.error("Failed to send reply:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleStatusChange = async (newStatus) => {
    if (changingStatus || newStatus === currentStatus || !id) return

    setChangingStatus(true)
    try {
      await dispatch(updateTicketStatus({
        ticketId: id,
        status: newStatus,
      })).unwrap()
      // Status will be updated automatically by Redux
    } catch (error) {
      console.error("Failed to change status:", error)
    } finally {
      setChangingStatus(false)
    }
  }

  const handleQuickAction = async (status) => {
    await handleStatusChange(status)
  }

  const handleAssignTicket = async (adminId) => {
    if (!id || !adminId || assigning) return
    
    setAssigning(true)
    try {
      await assignTicketToAdmin(id, adminId)
      // Refresh ticket data to show new assignment
      dispatch(getTicketWithReplies(id))
    } catch (error) {
      console.error("Failed to assign ticket:", error)
    } finally {
      setAssigning(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-gray-500">{error || "Ticket not found."}</p>
        <Button variant="outline" asChild>
          <Link to="/admin/support">Back to Tickets</Link>
        </Button>
      </div>
    )
  }

  const allMessages = getAllMessages()
  const customerName = ticket.createdBy
    ? `${ticket.createdBy.firstName || ""} ${ticket.createdBy.lastName || ""}`.trim() || ticket.createdBy.email || "Unknown"
    : "Unknown"
  const customerEmail = ticket.createdBy?.email || "N/A"

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 grid gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" asChild>
                <Link to="/admin/support">
                  <ArrowLeft className="h-5 w-5" />
                  <span className="sr-only">Back to tickets</span>
                </Link>
              </Button>
              <CardTitle className="text-2xl font-bold">Ticket #{ticket.ticket_id}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Select value={currentStatus} onValueChange={handleStatusChange} disabled={changingStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Change Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Answered">Answered</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardDescription className="px-6">Subject: {ticket.subject}</CardDescription>
          <CardContent className="pt-4">
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {allMessages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No messages yet.</p>
              ) : (
                allMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.isAdmin ? "justify-end" : "justify-start"}`}
                    style={{ marginLeft: `${(message.level || 0) * 24}px` }}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        message.isAdmin
                          ? "bg-primary text-white rounded-br-none"
                          : "bg-gray-100 text-gray-900 rounded-bl-none"
                      }`}
                    >
                      <div className="font-semibold text-sm mb-1">{message.isAdmin ? "You" : message.sender}</div>
                      <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                      <div className={`text-xs mt-1 ${message.isAdmin ? "text-blue-100" : "text-gray-500"}`}>
                        {formatDateTime(message.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-6">
              <Textarea
                placeholder="Type your reply here..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={4}
                className="mb-2"
                disabled={submitting || ticket.status === "Closed" || ticket.status === "Resolved"}
              />
              <Button onClick={handleSendReply} disabled={!replyContent.trim() || submitting || ticket.status === "Closed" || ticket.status === "Resolved"}>
                <Send className="h-4 w-4 mr-2" /> {submitting ? "Sending..." : "Send Reply"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-1 grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Ticket Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <span>
                Customer: <span className="font-medium">{customerName} ({customerEmail})</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              <span>
                Category: <span className="font-medium">{ticket.category}</span>
              </span>
            </div>
            {ticket.priority && (
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">Priority: {ticket.priority}</Badge>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Badge className={getStatusBadgeColor(currentStatus)}>Status: {currentStatus}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span>
                Created: <span className="font-medium">{formatDateTime(ticket.created_at)}</span>
              </span>
            </div>
            {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span>
                  Updated: <span className="font-medium">{formatDateTime(ticket.updated_at)}</span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-gray-500" />
              <span className="flex-1">Assigned To:</span>
              {loadingAdmins ? (
                <span className="text-gray-400">Loading...</span>
              ) : (
                <Select
                  value={ticket.assignedTo?.adminId ? String(ticket.assignedTo.adminId) : "unassigned"}
                  onValueChange={(value) => {
                    if (value === "unassigned") {
                      // Optionally handle unassignment
                      return
                    }
                    handleAssignTicket(parseInt(value))
                  }}
                  disabled={assigning}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Assign to admin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">
                      <span className="text-gray-500">Unassigned</span>
                    </SelectItem>
                    {admins.map((admin) => (
                      <SelectItem key={admin.id} value={String(admin.id)}>
                        {admin.name} {admin.email === ticket.assignedTo?.email && "(Current)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {ticket.assignedTo && (
              <div className="text-xs text-gray-500 mt-1 ml-6">
                Currently assigned to: {ticket.assignedTo.name || "Admin"}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2">
            <Button
              variant="outline"
              className="w-full bg-transparent"
              onClick={() => handleQuickAction("Resolved")}
              disabled={changingStatus || currentStatus === "Resolved"}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Mark as Resolved
            </Button>
            <Button
              variant="outline"
              className="w-full bg-transparent"
              onClick={() => handleQuickAction("Closed")}
              disabled={changingStatus || currentStatus === "Closed"}
            >
              <XCircle className="h-4 w-4 mr-2" /> Close Ticket
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
